import { useState } from 'react'
import { Eye, EyeOff, Plus, Trash2, UserPlus } from 'lucide-react'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { Field, Notice, StepCard, StepTitle, SubHeading } from '../../components/ui'
import { miscStateName } from '../../data/miscHandledStates'
import {
  type FilingsMadeUnder,
  type StatePortalRule,
  isSlipPlusState,
  mfaLabel,
  platformRulesForStates,
  portalRulesForState,
} from '../../data/stateLoginRules'
import {
  type PortalRequirement,
  credentialRowsForPortal,
  isCredentialRowComplete,
  missingPersonRows,
  newCredentialRow,
  platformCoveredStates,
  platformRequirement,
  statePortalRequirement,
} from '../../lib/credentials'
import type { BusinessEntity, CredentialRecord, CredentialRecordType } from '../../types/onboarding'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

const FILED_UNDER_LABEL: Record<FilingsMadeUnder, string> = {
  ENTITY: 'Filed under the entity',
  INDIVIDUAL: 'Separate login for each licensed individual',
  EITHER: 'Entity or individual (entity preferred)',
  VARIES: 'Varies by state',
}

function ownerValue(r: CredentialRecord): string {
  if (r.recordType === 'entity') return 'entity'
  return r.personId ? `${r.recordType}:${r.personId}` : ''
}

function parseOwner(value: string): { recordType: CredentialRecordType; personId: string | null } {
  if (value === 'entity') return { recordType: 'entity', personId: null }
  const [type, id] = value.split(':')
  return { recordType: type === 'drlp' ? 'drlp' : 'individual', personId: id ?? null }
}

function PasswordField({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [visible, setVisible] = useState(false)
  return (
    <Field
      label={label}
      type={visible ? 'text' : 'password'}
      value={value}
      autoComplete="new-password"
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" aria-label={visible ? 'Hide password' : 'Show password'} onClick={() => setVisible((v) => !v)}>
              {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}

function CredentialRow({ entity, record }: { entity: BusinessEntity; record: CredentialRecord }) {
  const { dispatch } = useOnboarding()
  const update = (patch: Partial<Omit<CredentialRecord, 'id' | 'portalId'>>) =>
    dispatch({ type: 'UPDATE_CREDENTIAL', entityId: entity.id, credentialId: record.id, patch })
  const complete = isCredentialRowComplete(record)

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, borderLeft: `3px solid ${complete ? '#059669' : '#e2e8f0'}`, pl: 1.5, py: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
        <Field
          select
          label="Login belongs to"
          value={ownerValue(record)}
          onChange={(e) => update(parseOwner(e.target.value))}
        >
          <MenuItem value="">
            <em>Select a person</em>
          </MenuItem>
          <MenuItem value="entity">Entity — {entity.name || 'Business entity'}</MenuItem>
          {entity.drlps.map((d, i) => (
            <MenuItem key={d.id} value={`drlp:${d.id}`}>
              DRLP — {d.fullName || `DRLP ${i + 1}`}
            </MenuItem>
          ))}
          {entity.individuals.map((p, i) => (
            <MenuItem key={p.id} value={`individual:${p.id}`}>
              Individual — {p.fullName || `Individual ${i + 1}`}
            </MenuItem>
          ))}
        </Field>
        <Field label="Username" value={record.username} autoComplete="off" onChange={(e) => update({ username: e.target.value })} />
        <PasswordField label="Password" value={record.password} onChange={(password) => update({ password })} />
        {record.recordType === 'entity' ? (
          <Field
            label="Security Questions"
            placeholder="Question / answer (if any)"
            value={record.securityQuestions}
            onChange={(e) => update({ securityQuestions: e.target.value })}
          />
        ) : null}
      </Box>
      <Tooltip title="Remove login">
        <IconButton aria-label="Remove login" onClick={() => dispatch({ type: 'REMOVE_CREDENTIAL', entityId: entity.id, credentialId: record.id })}>
          <Trash2 size={16} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

function RequirementChip({ requirement }: { requirement: PortalRequirement }) {
  const tone =
    requirement === 'required'
      ? { bg: '#dbeafe', color: '#1e3a8a', label: 'Login required' }
      : requirement === 'optional'
        ? { bg: '#f1f5f9', color: '#334155', label: 'Login optional' }
        : { bg: '#f1f5f9', color: '#64748b', label: 'No login needed' }
  return (
    <Box component="span" sx={{ borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 600, bgcolor: tone.bg, color: tone.color, whiteSpace: 'nowrap' }}>
      {tone.label}
    </Box>
  )
}

function PortalBlock({
  entity,
  rule,
  requirement,
  notNeededReason,
}: {
  entity: BusinessEntity
  rule: StatePortalRule
  requirement: PortalRequirement
  notNeededReason?: string
}) {
  const { dispatch } = useOnboarding()
  const rows = credentialRowsForPortal(entity, rule.id)
  const missingPeople = rule.filingsMadeUnder === 'INDIVIDUAL' ? missingPersonRows(entity, rule) : []
  const mfa = mfaLabel(rule.mfa)
  const disabled = requirement === 'notNeeded'

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: disabled ? '#f8fafc' : '#ffffff', p: 2, opacity: disabled ? 0.75 : 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: disabled ? '#64748b' : '#0f172a' }}>{rule.system}</Typography>
        <RequirementChip requirement={requirement} />
        {!disabled ? (
          <>
            <Box component="span" sx={{ fontSize: 12, color: '#475569' }}>
              {FILED_UNDER_LABEL[rule.filingsMadeUnder]}
            </Box>
            {mfa ? (
              <Box component="span" sx={{ fontSize: 12, color: '#92400e' }}>
                · {mfa}
              </Box>
            ) : null}
          </>
        ) : null}
        <Link href={rule.portalUrl} target="_blank" rel="noopener noreferrer" sx={{ ml: 'auto', fontSize: 12, fontWeight: 600 }}>
          Open portal
        </Link>
      </Box>

      {disabled ? (
        <Typography sx={{ mt: 1, fontSize: 13, color: '#64748b' }}>{notNeededReason}</Typography>
      ) : (
        <>
          {rule.info ? <Typography sx={{ mt: 1, fontSize: 12, lineHeight: 1.6, color: '#475569' }}>{rule.info}</Typography> : null}
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {rows.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#92400e' }}>No logins entered yet for this system.</Typography>
            ) : null}
            {rows.map((r) => (
              <CredentialRow key={r.id} entity={entity} record={r} />
            ))}
          </Stack>
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              type="button"
              size="small"
              startIcon={<Plus size={14} />}
              onClick={() =>
                dispatch({
                  type: 'ADD_CREDENTIALS',
                  entityId: entity.id,
                  rows: [newCredentialRow(rule.id, rule.filingsMadeUnder === 'INDIVIDUAL' ? 'individual' : 'entity', null)],
                })
              }
            >
              Add another login
            </Button>
            {missingPeople.length > 0 ? (
              <Button
                type="button"
                size="small"
                startIcon={<UserPlus size={14} />}
                onClick={() => dispatch({ type: 'ADD_CREDENTIALS', entityId: entity.id, rows: missingPeople })}
              >
                Add a login for each licensed individual ({missingPeople.length})
              </Button>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  )
}

function WritingBusinessToggle({ entity, stateCode }: { entity: BusinessEntity; stateCode: string }) {
  const { dispatch } = useOnboarding()
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ fontSize: 14, color: '#334155' }}>
        Are you currently writing business in {miscStateName(stateCode)}?
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={entity.writingBusiness[stateCode] ?? null}
        onChange={(_, answer) => {
          if (answer) dispatch({ type: 'SET_WRITING_BUSINESS', entityId: entity.id, stateCode, answer })
        }}
        aria-label={`Writing business in ${miscStateName(stateCode)}`}
      >
        <ToggleButton value="yes" sx={{ px: 2, textTransform: 'none', '&.Mui-selected': { bgcolor: '#dbeafe', color: MISC_PRIMARY } }}>
          Yes
        </ToggleButton>
        <ToggleButton value="no" sx={{ px: 2, textTransform: 'none', '&.Mui-selected': { bgcolor: '#f1f5f9', color: '#0f172a' } }}>
          No
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}

function StateCard({ entity, stateCode }: { entity: BusinessEntity; stateCode: string }) {
  const answer = entity.writingBusiness[stateCode]
  const portals = portalRulesForState(stateCode)
  const requirements = portals.map((rule) => statePortalRequirement(rule, answer))
  const allNotNeeded = requirements.length > 0 && requirements.every((r) => r === 'notNeeded')
  const name = miscStateName(stateCode)

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: allNotNeeded ? '#e2e8f0' : answer ? '#bfdbfe' : '#e2e8f0',
        bgcolor: allNotNeeded ? '#f8fafc' : '#ffffff',
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: allNotNeeded ? '#64748b' : '#0f172a' }}>{name}</Typography>
        <WritingBusinessToggle entity={entity} stateCode={stateCode} />
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {!answer ? (
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>Answer the question above to see which logins MISC needs for {name}.</Typography>
        ) : portals.length > 0 ? (
          <Stack spacing={1.5}>
            {allNotNeeded ? (
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                No login needed — you are not writing business in {name}, and {name} does not require a login for zero reports.
              </Typography>
            ) : (
              portals.map((rule, i) => (
                <PortalBlock
                  key={rule.id}
                  entity={entity}
                  rule={rule}
                  requirement={requirements[i] as PortalRequirement}
                  notNeededReason={`Not needed — ${name} ${rule.system} does not require a login for zero reports.`}
                />
              ))
            )}
            {answer === 'no' && !allNotNeeded && requirements.some((r) => r === 'required') ? (
              <Typography sx={{ fontSize: 12, color: '#475569' }}>
                {name} requires zero reports, so MISC still needs a login even though you are not writing business there.
              </Typography>
            ) : null}
          </Stack>
        ) : isSlipPlusState(stateCode) ? (
          <Typography sx={{ fontSize: 13, color: '#475569' }}>
            {name} is filed through <strong>SLIP+ for States</strong> — enter that login under Multi-State Filing Platforms below.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: '#475569' }}>
            {name} does not have a state-specific system on MISC&apos;s State Logins guide, so it is filed through{' '}
            <strong>OPTins</strong> — enter that login under Multi-State Filing Platforms below.
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * Design manual p.7 — State Login Credentials, with the call's conditional logic.
 */
export function EntityCredentials({ entity }: { entity: BusinessEntity }) {
  const platforms = platformRulesForStates(entity.statesToHandle)

  return (
    <StepCard>
      <StepTitle subtitle="Please provide filing portal credentials required for compliance reporting. Only the states you selected for this entity are shown.">
        Filing System Credentials
      </StepTitle>

      {entity.statesToHandle.length === 0 ? (
        <Notice tone="warning">No states selected for this entity yet. Go to the States section to choose the states MISC will handle.</Notice>
      ) : (
        <Stack spacing={2}>
          <Notice tone="neutral">
            For each state, tell us whether this entity is currently writing business there. If you are, MISC needs a login.
            If you are not, MISC only needs a login where the state requires zero reports — other states are greyed out.
            Systems that issue a login to each licensed individual list one row per person; add as many logins as you need.
          </Notice>

          {entity.statesToHandle.map((code) => (
            <StateCard key={code} entity={entity} stateCode={code} />
          ))}

          {platforms.length > 0 ? (
            <Box sx={{ pt: 1 }}>
              <SubHeading>Multi-State Filing Platforms</SubHeading>
              <Stack spacing={1.5}>
                {platforms.map((rule) => {
                  const requirement = platformRequirement(entity, rule)
                  const covered = platformCoveredStates(rule, entity.statesToHandle)
                  return requirement ? (
                    <PortalBlock
                      key={rule.id}
                      entity={entity}
                      rule={{ ...rule, system: `${rule.stateLabel} (${covered.map(miscStateName).join(', ')})` }}
                      requirement={requirement}
                      notNeededReason="Not needed for the states above."
                    />
                  ) : (
                    <Box key={rule.id} sx={{ borderRadius: 2, border: '1px dashed #cbd5e1', p: 2, fontSize: 13, color: '#64748b' }}>
                      <strong>{rule.stateLabel}</strong> — answer the writing-business question for{' '}
                      {covered.map(miscStateName).join(', ')} first.
                    </Box>
                  )
                })}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      )}
    </StepCard>
  )
}
